"use client";

import {
  THEATER_OVERLAY_PAGE_ATTR,
  isTheaterOverlayFrozen,
  subscribeTheaterOverlayFreeze,
} from "@/lib/theater-overlay-freeze";
import { cn } from "@/lib/utils";
import { type ReactNode, useLayoutEffect, useRef, useSyncExternalStore } from "react";

export function useTheaterOverlayFrozen(): boolean {
  return useSyncExternalStore(subscribeTheaterOverlayFreeze, isTheaterOverlayFrozen, () => false);
}

function pauseSubtreeWork(root: HTMLElement): () => void {
  const media = Array.from(root.querySelectorAll("video, audio")).filter(
    (el): el is HTMLMediaElement => el instanceof HTMLMediaElement && !el.paused
  );
  for (const el of media) {
    el.pause();
  }

  const withAnimations = root as HTMLElement & {
    getAnimations?: (options?: { subtree?: boolean }) => Animation[];
  };
  let animations: Animation[] = [];
  if (typeof withAnimations.getAnimations === "function") {
    try {
      animations = withAnimations
        .getAnimations({ subtree: true })
        .filter((animation) => animation.playState === "running");
      for (const animation of animations) {
        animation.pause();
      }
    } catch {
      animations = [];
    }
  }

  return () => {
    for (const animation of animations) {
      try {
        animation.play();
      } catch {
        /* animation may have finished while frozen */
      }
    }
  };
}

/**
 * Listing/page tree under the intercept overlay.
 * Always wrap children (never mount/unmount this on freeze) so React state and
 * scroll stay put. Must be a real box — not `display: contents` — so inert and
 * content-visibility apply on every viewport.
 */
export function TheaterOverlayFrozenPage({ children }: { children: ReactNode }) {
  const frozen = useTheaterOverlayFrozen();
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!frozen || !rootRef.current) return;
    return pauseSubtreeWork(rootRef.current);
  }, [frozen]);

  return (
    <div
      ref={rootRef}
      data-testid="theater-overlay-frozen-page"
      {...{ [THEATER_OVERLAY_PAGE_ATTR]: "" }}
      inert={frozen ? true : undefined}
      aria-hidden={frozen ? true : undefined}
      className={cn(frozen && "theater-overlay-frozen")}
    >
      {children}
    </div>
  );
}
